import { styled } from "@material-ui/core";
import { useEffect } from "react";
import { brand } from "../theme";

export type LottoLocoBannerProps = {
  onPlay: () => void;
};

const STYLE_TAG_ID = "lotto-loco-banner-keyframes";
const KEYFRAMES_CSS = `
@keyframes lottoLocoSparkle { 0%, 100% { transform: scale(1) rotate(0); opacity: 0.7; } 50% { transform: scale(1.3) rotate(15deg); opacity: 1; } }
@keyframes lottoLocoShake { 0%, 100% { transform: rotate(0); } 25% { transform: rotate(-3deg); } 75% { transform: rotate(3deg); } }
@keyframes lottoLocoPulse {
  0%, 100% { box-shadow: 0 4px 18px rgba(245,166,35,0.4), 0 0 0 0 rgba(245,166,35,0.6); }
  50% { box-shadow: 0 4px 22px rgba(245,166,35,0.55), 0 0 0 8px rgba(245,166,35,0); }
}
`;

const ensureKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_TAG_ID)) return;
  const tag = document.createElement("style");
  tag.id = STYLE_TAG_ID;
  tag.textContent = KEYFRAMES_CSS;
  document.head.appendChild(tag);
};

const Root = styled("div")(({ theme }) => ({
  margin: theme.spacing(1.25, 1.5, 0.75),
  borderRadius: 14,
  background: `linear-gradient(135deg, ${brand.amberDark} 0%, #2D1F08 100%)`,
  padding: theme.spacing(1.5, 1.75),
  display: "flex",
  alignItems: "center",
  gap: 12,
  position: "relative",
  overflow: "hidden",
  border: `1px solid ${brand.amber}44`,
  "& .sparkle": {
    position: "absolute",
    color: brand.amber,
    pointerEvents: "none",
    animationName: "lottoLocoSparkle",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
  },
  "& .sparkle--a": {
    top: 6,
    right: 50,
    fontSize: 10,
    opacity: 0.6,
    animationDuration: "2.4s",
  },
  "& .sparkle--b": {
    bottom: 8,
    right: 90,
    fontSize: 8,
    opacity: 0.5,
    animationDuration: "1.8s",
  },
  "& .sparkle--c": {
    top: 14,
    right: 140,
    fontSize: 7,
    opacity: 0.4,
    animationDuration: "3s",
  },
  "& .icon": {
    width: 42,
    height: 42,
    borderRadius: 11,
    background: `linear-gradient(135deg, ${brand.amber}, ${brand.amberDeep})`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    flexShrink: 0,
    boxShadow: `0 4px 14px ${brand.amber}88`,
    animation: "lottoLocoShake 3s ease-in-out infinite",
  },
  "& .copy": {
    flex: 1,
    minWidth: 0,
  },
  "& .title-row": {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  "& .title": {
    fontSize: 14,
    fontWeight: 800,
    color: brand.amber,
    letterSpacing: 0.3,
  },
  "& .new-pill": {
    fontSize: 8,
    fontWeight: 800,
    color: brand.amberDark,
    background: brand.amber,
    padding: "2px 5px",
    borderRadius: 3,
    letterSpacing: 0.6,
  },
  "& .subtitle": {
    fontSize: 11,
    color: "#C4B8A8",
    marginTop: 1,
    lineHeight: 1.3,
  },
  "& .play": {
    background: brand.amber,
    color: brand.amberDark,
    border: "none",
    padding: "9px 14px",
    borderRadius: 9,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.6,
    cursor: "pointer",
    textTransform: "uppercase",
    animation: "lottoLocoPulse 2.2s ease-in-out infinite",
    flexShrink: 0,
    fontFamily: "inherit",
  },
}));

const LottoLocoBanner = ({ onPlay }: LottoLocoBannerProps) => {
  useEffect(() => {
    ensureKeyframes();
  }, []);
  return (
    <Root data-testid="lotto-loco-banner">
      <span className="sparkle sparkle--a" aria-hidden>✦</span>
      <span className="sparkle sparkle--b" aria-hidden>✦</span>
      <span className="sparkle sparkle--c" aria-hidden>✦</span>
      <div className="icon" aria-hidden>🎰</div>
      <div className="copy">
        <div className="title-row">
          <span className="title">LOTTO LOCO</span>
          <span className="new-pill">NEW</span>
        </div>
        <div className="subtitle">
          Feeling lucky? Let fate fill all your rounds.
        </div>
      </div>
      <button
        type="button"
        className="play"
        onClick={onPlay}
        data-testid="lotto-loco-play"
      >
        Play
      </button>
    </Root>
  );
};

export default LottoLocoBanner;
